/**
 * ProductVariants - Pure JavaScript implementation for Shopify variant picker
 * Handles variant selection and URL updates without external dependencies
 */

/* global Swiper */
class ProductVariants extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.handleVariantChange();
    this.updatePriceDisplay();
    // Initialize option prices on page load
    this.updateOptionPrices();
    // Initialize variant IDs on page load
    this.updateVariantIds();
    // Initialize saving badge content on page load
    this.updateSavingBadgeContent();
    // Initialize saving badges on page load
    const currentVariant = this.getCurrentVariant();
    this.updateSavingBadges(currentVariant);
    this.initializeSavingBadgeStates();
    this.updateBuyButtonState();
  }

  /**
   * Update browser URL with selected variant
   */
  updateURL(variantId) {
    const productUrl = this.dataset.url;
    const newUrl = variantId
      ? `${productUrl}?variant=${variantId}`
      : productUrl;

    window.history.replaceState({}, "", newUrl);
  }

  /**
   * Get currently selected variant based on form inputs
   */
  getCurrentVariant() {
    const form = this.querySelector("form") || this.closest("form");
    if (!form) return null;

    const selectedOptions = [];
    const optionInputs = this.querySelectorAll(
      'input[name^="options["]:checked'
    );

    optionInputs.forEach((input) => {
      const position = input.dataset.optionPosition;
      const value = input.value;
      selectedOptions[position - 1] = value;
    });

    // Find matching variant
    const variants = this.getProductVariants();
    return variants.find((variant) => {
      return variant.options.every((option, index) => {
        return option === selectedOptions[index];
      });
    });
  }

  /**
   * Get product variants from JSON data
   */
  getProductVariants() {
    const productDataScript = document.querySelector("#product-json");
    if (productDataScript) {
      try {
        const productData = JSON.parse(productDataScript.textContent);
        return productData.variants || [];
      } catch (error) {
        console.error("Error parsing product data:", error);
      }
    }
    return [];
  }

  /**
   * Update variant ID in hidden input
   */
  updateVariantId(variantId) {
    const input = document.querySelector(
      '.product__add-to-cart input[name="id"].product-variant-id'
    );
    if (input) {
      input.value = variantId;
    }
  }

  /**
   * Update price display when variant changes
   */
  updatePriceDisplay() {
    const currentVariant = this.getCurrentVariant();
    const priceElement = document.querySelector(".main-product__price--price");
    const compareElement = document.querySelector(
      ".main-product__price--compare"
    );
    const discountElement = document.querySelector(
      ".main-product__price--discount"
    );
    const discountBadge = document.querySelector("[data-discount-badge]");
    const savingsAmount = document.querySelector("[data-savings-amount]");

    if (!currentVariant || !priceElement) return;

    // Update main price
    priceElement.textContent = this.formatMoney(currentVariant.price);

    // update subs price
    if(!document.querySelector("[no-sub]")){
      const discount = Math.abs(((+selling_plan.selectedOptions[0].dataset.discount) / 100) - 1)
      document.querySelector(".sp-info .sp-compare, .sp-info .sp-price.one").textContent = `${this.formatMoney(currentVariant.price)}`
      document.querySelector(".sp-info .sp-price:not(.one)").textContent = `${this.formatMoney(currentVariant.price * discount)}`
    }

    // Update compare at price
    if (compareElement) {
      if (
        currentVariant.compare_at_price &&
        currentVariant.compare_at_price > currentVariant.price
      ) {
        compareElement.textContent = this.formatMoney(
          currentVariant.compare_at_price
        );
        compareElement.style.display = "inline";
      } else {
        compareElement.style.display = "none";
      }
    }

    // Update discount percentage
    if (
      discountElement &&
      currentVariant.compare_at_price &&
      currentVariant.compare_at_price > currentVariant.price
    ) {
      const discount = currentVariant.compare_at_price - currentVariant.price;
      const discountPercentage = Math.round(
        (discount / currentVariant.compare_at_price) * 100
      );
      discountElement.textContent = `${discountPercentage}% OFF`;
      discountElement.style.display = "inline";
    } else if (discountElement) {
      discountElement.style.display = "none";
    }

    // Update discount badge (You save $X.XX)
    if (
      currentVariant.compare_at_price &&
      currentVariant.compare_at_price > currentVariant.price
    ) {
      const savings = currentVariant.compare_at_price - currentVariant.price;

      if (discountBadge) {
        discountBadge.style.display = "flex";
      }

      if (savingsAmount) {
        savingsAmount.textContent = this.formatMoney(savings);
      }
    } else {
      if (discountBadge) {
        discountBadge.style.display = "none";
      }
    }

    // Update variant ID in hidden input
    const variantIdInput = document.querySelector(
      '.product__add-to-cart input[name="id"].product-variant-id'
    );
    if (variantIdInput) {
      variantIdInput.value = currentVariant.id;
    }

    // Update saving badges for all variant options
    this.updateSavingBadges(currentVariant);
  }

  /**
   * Handle variant option changes
   */
  handleVariantChange() {
    const variantInputs = this.querySelectorAll("input.product__variant-input");

    variantInputs.forEach((input) => {
      input.addEventListener("change", () => {
        // Update visual states
        this.updateVariantLabels();

        // Update variant IDs in data attributes
        this.updateVariantIds();

        // Update option prices dynamically
        this.updateOptionPrices();

        // Update price and availability
        this.updatePriceDisplay();
        this.updateAvailabilityDisplay();
        this.updateBuyButtonState();

        // Update saving badges immediately on change
        const currentVariant = this.getCurrentVariant();
        this.updateSavingBadges(currentVariant);

        // Update saving badge content after badges logic (this should be last for badges)
        this.updateSavingBadgeContent();

        // Update variant ID in form for add to cart
        if (currentVariant) {
          this.updateVariantId(currentVariant.id);
          // Update URL
          this.updateURL(currentVariant.id);
          // Update gallery image
          this.updateGalleryImage(currentVariant);
        }

        // Trigger custom event for other components
        this.dispatchEvent(
          new CustomEvent("variant:changed", {
            detail: { variant: currentVariant },
            bubbles: true,
          })
        );
      });
    });
  }

  /**
   * Update option prices dynamically based on current selection
   */
  updateOptionPrices() {
    const variants = this.getProductVariants();
    const currentSelections = this.getCurrentSelections();

    // Get all option inputs
    const allOptions = this.querySelectorAll("input.product__variant-input");

    allOptions.forEach((input) => {
      const position = parseInt(input.dataset.optionPosition) - 1;
      const value = input.value;
      const priceElement = input.parentElement.querySelector(
        ".variant-option-price"
      );

      if (priceElement) {
        // Find matching variant with current selections + this option
        const testSelections = [...currentSelections];
        testSelections[position] = value;

        const matchingVariant = variants.find((variant) => {
          return variant.options.every((option, index) => {
            return option === testSelections[index];
          });
        });

        if (matchingVariant) {
          priceElement.textContent = this.formatMoney(matchingVariant.price);
          priceElement.dataset.price = matchingVariant.price;
        }
      }
    });
  }

  /**
   * Update saving badges for variant options
   */
  updateSavingBadges() {
    const savingBadges = this.querySelectorAll("[data-saving-badge]");
    const variants = this.getProductVariants();
    const currentSelections = this.getCurrentSelections();

    savingBadges.forEach((badge) => {
      const variantId = badge.dataset.variantId;
      const targetVariant = variants.find((v) => v.id == variantId); // Use == for type coercion

      if (!targetVariant) {
        badge.style.display = "none";
        return;
      }

      // Check if this badge is for a Size option
      const variantPicker = badge.closest(".product__variant-picker");
      const parentInput = variantPicker?.querySelector(".product__variant-title--name");

      if (!parentInput) {
        badge.style.display = "none";
        return;
      }

      const optionName = parentInput.textContent?.trim() || "";

      // Only show saving badges for Size or Pack Size options
      if (optionName !== "Size" && optionName !== "Pack Size") {
        badge.style.display = "none";
        return;
      }

      // Check if this variant matches the current style selection
      // but could be any size within that style
      let shouldShow = true;

      // Compare each option except for the size option (position 1)
      for (let i = 0; i < currentSelections.length; i++) {
        const isSizeOption = i === 1; // Size is at position 1 (0-indexed)

        // Only compare non-size options (Style, Color, etc.)
        if (
          !isSizeOption &&
          currentSelections[i] &&
          targetVariant.options[i] !== currentSelections[i]
        ) {
          shouldShow = false;
          break;
        }
      }

      if (shouldShow) {
        // Only show if it has metafield content
        const hasMetafield = badge.getAttribute("data-has-metafield") === "true";

        if (hasMetafield) {
          badge.style.display = "block";
          badge.style.opacity = "1";
        } else {
          badge.style.display = "none";
        }
      } else {
        badge.style.display = "none";
      }
    });
  }
  /**
   * Get product metafields from JSON data
   */
  getProductMetafields() {
    const metafieldsScript = document.querySelector("#product-metafields");
    if (metafieldsScript) {
      try {
        return JSON.parse(metafieldsScript.textContent);
      } catch (error) {
        console.error("Error parsing product metafields:", error);
      }
    }
    return {};
  }

  /**
   * Get variant metafields from JSON data
   */
  getVariantMetafields() {
    const metafieldsScript = document.querySelector("#variant-metafields");
    if (metafieldsScript) {
      try {
        return JSON.parse(metafieldsScript.textContent);
      } catch (error) {
        console.error("Error parsing variant metafields:", error);
      }
    }
    return {};
  }

  /**
   * Update saving badge content based on variant metafields
   */
  updateSavingBadgeContent() {
    const metafields = this.getVariantMetafields();
    const savingBadges = this.querySelectorAll("[data-saving-badge]");

    savingBadges.forEach((badge) => {
      const variantId = badge.getAttribute("data-variant-id");

      // Check if this badge is for a Size option (same logic as updateSavingBadges)
      const parentInput = badge
        .closest(".product__variant-picker")
        .querySelector(".product__variant-title--name");

      // Only update content for Size or Pack Size options
      if (
        parentInput &&
        parentInput.textContent !== "Size" &&
        parentInput.textContent !== "Pack Size"
      ) {
        badge.textContent = "";
        badge.style.display = "none";
        badge.setAttribute("data-has-metafield", "false");
        return;
      }

      if (
        variantId &&
        metafields[variantId] &&
        metafields[variantId].saving_badge
      ) {
        badge.textContent = metafields[variantId].saving_badge;
        // Mark that this badge has content
        badge.setAttribute("data-has-metafield", "true");
      } else {
        // Clear the content when no metafield exists and force hide
        badge.textContent = "";
        badge.style.display = "none";
        badge.style.setProperty("display", "none", "important");
        // Mark that this badge has no content
        badge.setAttribute("data-has-metafield", "false");
      }
    });
  }

  /**
   * Get option name from input element
   */
  getOptionNameFromInput(input) {
    // Extract option name from input name attribute
    // name="options[Size]" -> "Size"
    const nameAttr = input.getAttribute("name");
    const match = nameAttr.match(/options\[([^\]]+)\]/);
    return match ? match[1] : null;
  }

  /**
   * Get current selections array
   */
  getCurrentSelections() {
    const selectedOptions = [];
    const optionInputs = this.querySelectorAll(
      'input[name^="options["]:checked'
    );

    optionInputs.forEach((input) => {
      const position = parseInt(input.dataset.optionPosition) - 1;
      selectedOptions[position] = input.value;
    });

    return selectedOptions;
  }

  /**
   * Update variant label visual states
   */
  updateVariantLabels() {
    const variantInputs = this.querySelectorAll("input.product__variant-input");

    variantInputs.forEach((input) => {
      const label = document.querySelector(`label[for="${input.id}"]`);
      const savingBadge = label
        .closest(".product__variant-item")
        .querySelector("[data-saving-badge]");
      if (label) {
        // Remove all state classes
        label.classList.remove("selected", "disabled", "unavailable");

        // Add appropriate state class
        if (input.checked) {
          label.classList.add("selected");
        }

        if (input.disabled) {
          label.classList.add("disabled");
        }

        // Check availability
        if (!this.isOptionAvailable(input)) {
          label.classList.add("unavailable");
        }
      }

      // Update saving badge visibility based on label state
      if (savingBadge) {
        savingBadge.classList.remove("selected");
      }

      if (input.checked && savingBadge) {
        savingBadge.classList.add("selected");
      }
    });
  }

  /**
   * Initialize saving badge selected states on page load
   */
  initializeSavingBadgeStates() {
    const selectedInputs = this.querySelectorAll(
      "input.product__variant-input:checked"
    );

    selectedInputs.forEach((input) => {
      const savingBadge = input
        .closest(".product__variant-item")
        .querySelector("[data-saving-badge]");
      if (savingBadge) {
        savingBadge.classList.add("selected");
      }
    });
  }

  /**
   * Check if a variant can be purchased
   */
  isVariantPurchasable(variant) {
    if (!variant) return false;

    const productMeta = this.getProductMetafields();
    const variantMeta = this.getVariantMetafields()[variant.id];

    if (productMeta?.manually_disabled) return false;
    if (variantMeta?.manually_disabled) return false;

    return variant.available;
  }

  /**
   * Check if option combination is available
   */
  isOptionAvailable(optionInput) {
    const variants = this.getProductVariants();
    const position = parseInt(optionInput.dataset.optionPosition) - 1;
    const value = optionInput.value;

    return variants.some((variant) => {
      return (
        variant.options[position] === value &&
        this.isVariantPurchasable(variant)
      );
    });
  }

  /**
   * Update buy button state when variant changes
   */
  updateBuyButtonState() {
    const currentVariant = this.getCurrentVariant();
    const purchasable = this.isVariantPurchasable(currentVariant);
    const buyButton = document.querySelector("[data-buy-button]");
    const variantIdInput = document.querySelector(
      '.product__add-to-cart input[name="id"].product-variant-id'
    );

    if (buyButton) {
      buyButton.disabled = !purchasable;
      buyButton.type = purchasable ? "submit" : "button";
      buyButton.textContent = purchasable ? "Add to Cart" : "Sold Out";
      buyButton.classList.toggle("btn-red", purchasable);
      buyButton.classList.toggle("add-to-cart", purchasable);
    }

    if (variantIdInput) {
      variantIdInput.disabled = !purchasable;
    }
  }

  /**
   * Update availability display
   */
  updateAvailabilityDisplay() {
    const currentVariant = this.getCurrentVariant();
    const availabilityElement = document.querySelector(".availability");

    if (availabilityElement && currentVariant) {
      availabilityElement.classList.remove("available", "unavailable");

      if (this.isVariantPurchasable(currentVariant)) {
        availabilityElement.classList.add("available");
        availabilityElement.textContent = "In stock";
      } else {
        availabilityElement.classList.add("unavailable");
        availabilityElement.textContent = "Out of stock";
      }
    }
  }

  /**
   * Format money according to shop currency
   */
  formatMoney(cents) {
    const amount = cents / 100;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD", // This should be dynamic based on shop currency
    }).format(amount);
  }

  /**
   * Update variant IDs in data attributes when selections change
   */
  updateVariantIds() {
    const variants = this.getProductVariants();
    const currentSelections = this.getCurrentSelections();

    // Get all variant items
    const variantItems = this.querySelectorAll(".product__variant-item");

    variantItems.forEach((item) => {
      const input = item.querySelector("input.product__variant-input");
      if (!input) return;

      const position = parseInt(input.dataset.optionPosition) - 1;
      const value = input.value;

      // Create test selections with this option value
      const testSelections = [...currentSelections];
      testSelections[position] = value;

      // Find matching variant
      let targetVariant = variants.find((variant) => {
        return variant.options.every((option, index) => {
          return option === testSelections[index];
        });
      });

      // If no exact match, find any variant with this option value
      if (!targetVariant) {
        targetVariant = variants.find((variant) => {
          return variant.options[position] === value;
        });
      } // Update data-variant-id attribute
      if (targetVariant) {
        item.setAttribute("data-variant-id", targetVariant.id);

        // Also update saving badge variant ID if it exists
        const savingBadge = item.querySelector("[data-saving-badge]");
        if (savingBadge) {
          savingBadge.setAttribute("data-variant-id", targetVariant.id);
        }
      } else {
        item.setAttribute("data-variant-id", "");
      }
    });

    // Update saving badge content after IDs are updated
    this.updateSavingBadgeContent();
  }

  /**
   * Update gallery image when variant changes
   */
  updateGalleryImage(currentVariant) {
    if (!currentVariant) return;

    this.updateGalleryThumbnails(currentVariant);

    // Determine correct Swiper instances (PDP vs quick-view)
    const isQuickView = !!this.closest('.quick-view-content');
    const mainSwiper = isQuickView ? window.quickViewGalleryMain : window.productGalleryMain;
    const thumbsSwiper = isQuickView ? window.quickViewGalleryThumbs : window.productGalleryThumbs;

    // Navigate to first slide (variant image is always first after update)
    if (mainSwiper) mainSwiper.slideTo(0);
    if (thumbsSwiper) thumbsSwiper.slideTo(0);
  }

  /**
   * Update gallery thumbnails to show only selected variant + general images.
   * Physically moves slides in/out of the Swiper wrapper instead of display:none
   * because hidden slides break Swiper's spaceBetween position calculation.
   */
  updateGalleryThumbnails(currentVariant) {
    // Scope to the correct gallery (PDP vs quick-view)
    const quickViewContent = this.closest('.quick-view-content');
    const context = quickViewContent || document;
    const gallery = context.querySelector('.product-gallery');
    if (!gallery) return;

    const mainWrapper = gallery.querySelector('.product-gallery__main .swiper-wrapper');
    const thumbsWrapper = gallery.querySelector('.product-gallery__thumbs .swiper-wrapper');
    if (!mainWrapper) return;

    // Get or create storage containers for inactive variant slides
    let mainStorage = gallery.querySelector('.gallery-hidden-main');
    if (!mainStorage) {
      mainStorage = document.createElement('div');
      mainStorage.className = 'gallery-hidden-main';
      mainStorage.style.display = 'none';
      gallery.appendChild(mainStorage);
    }

    let thumbsStorage = gallery.querySelector('.gallery-hidden-thumbs');
    if (!thumbsStorage) {
      thumbsStorage = document.createElement('div');
      thumbsStorage.className = 'gallery-hidden-thumbs';
      thumbsStorage.style.display = 'none';
      gallery.appendChild(thumbsStorage);
    }

    // Move all variant slides from wrapper to storage
    mainWrapper.querySelectorAll('[data-image-type="variant"]').forEach(function(slide) {
      slide.style.display = '';
      mainStorage.appendChild(slide);
    });
    if (thumbsWrapper) {
      thumbsWrapper.querySelectorAll('[data-image-type="variant"]').forEach(function(slide) {
        slide.style.display = '';
        thumbsStorage.appendChild(slide);
      });
    }

    // Move only the selected variant's slides back to wrapper (at beginning)
    if (currentVariant && currentVariant.id) {
      const variantId = String(currentVariant.id);
      const selectedMain = mainStorage.querySelector('[data-image-type="variant"][data-variant-id="' + variantId + '"]');
      const selectedThumb = thumbsStorage ? thumbsStorage.querySelector('[data-image-type="variant"][data-variant-id="' + variantId + '"]') : null;

      if (selectedMain) {
        mainWrapper.insertBefore(selectedMain, mainWrapper.firstChild);
      }
      if (selectedThumb && thumbsWrapper) {
        thumbsWrapper.insertBefore(selectedThumb, thumbsWrapper.firstChild);
      }
    }

    // Update correct Swiper instances
    const isQuickView = !!quickViewContent;
    const mainSwiper = isQuickView ? window.quickViewGalleryMain : window.productGalleryMain;
    const thumbsSwiper = isQuickView ? window.quickViewGalleryThumbs : window.productGalleryThumbs;

    if (mainSwiper) mainSwiper.update();
    if (thumbsSwiper) thumbsSwiper.update();
  }
}

// Register custom element
customElements.define("product-variants", ProductVariants);

const initProductVariants = () => {
};

export { initProductVariants };
